import { FC } from 'react';
import classNames from 'classnames';

import './InlineSpinner.scss';

export type InlineSpinnerSize = 'small' | 'large';
export type InlineSpinnerSurface = 'default' | 'inverse' | 'inherit';

export interface InlineSpinnerProps {
  /** Accessibility label announced by screen readers. Default 'Loading'. */
  label?: string;
  /** Optional className for layout/positioning by the consumer. */
  className?: string;
  /** Spinner size. `small` = 16px, `large` = 24px. Default `small`. */
  size?: InlineSpinnerSize;
  /** Color theming. `default` for light surfaces, `inverse` for dark, `inherit` to follow the parent's text color. Default `default`. */
  surface?: InlineSpinnerSurface;
}

/**
 * InlineSpinner component — a rotating indicator used inside fixed containers
 * such as buttons and cards. For page-level loading use `LoadingOverlay`.
 * @param label - Accessibility label announced by screen readers. Default 'Loading'.
 * @param className - Optional className for layout/positioning.
 * @param size - `small` (16px) or `large` (24px). Default `small`.
 * @param surface - `default`, `inverse`, or `inherit` color theming. Default `default`.
 */
export const InlineSpinner: FC<InlineSpinnerProps> = ({
  label = 'Loading',
  className,
  size = 'small',
  surface = 'default',
}) => {
  return (
    <span
      className={classNames(
        'vds-inline-spinner',
        `vds-inline-spinner--size-${size}`,
        `vds-inline-spinner--surface-${surface}`,
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        className="vds-inline-spinner__svg"
        aria-hidden="true">
        <path
          className="vds-inline-spinner__track"
          fillRule="evenodd"
          d="M12 3.3a8.7 8.7 0 1 0 0 17.4 8.7 8.7 0 0 0 0-17.4M0 12a12 12 0 1 1 24 0 12 12 0 0 1-24 0"
          clipRule="evenodd"
        />
        <path
          className="vds-inline-spinner__arc"
          d="M24 12A12 12 0 0 0 12 0a1.6 1.6 0 0 0 0 3.3 8.7 8.7 0 0 1 5.7 15.3 1.6 1.6 0 1 0 2.1 2.5A12 12 0 0 0 24 12"
        />
      </svg>
    </span>
  );
};
